import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuth } from '@/lib/auth';
import { AuthProvider } from '@/lib/auth';

// Pages
import { DashboardPage } from '@/pages/DashboardPage';
import { CRMPage } from '@/pages/CRMPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { EstimatesPage } from '@/pages/EstimatesPage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { AIPage } from '@/pages/AIPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';

// Auth layouts
function AuthLayout(): React.ReactElement {
  return <Outlet />;
}

function ProtectedLayout(): React.ReactElement {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#020203]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect handled by route loader
    return null;
  }

  return (
    <ErrorBoundary>
      <AppShell>
        <Outlet />
      </AppShell>
    </ErrorBoundary>
  );
}

// Root route
const rootRoute = createRootRoute({
  component: () => (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  ),
});

// Auth routes (no AppShell)
const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      throw redirect({ to: '/' });
    }
  },
});

const registerRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/register',
  component: RegisterPage,
  beforeLoad: () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      throw redirect({ to: '/' });
    }
  },
});

// Protected routes (with AppShell)
const protectedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
  beforeLoad: () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw redirect({ to: '/login' });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/',
  component: DashboardPage,
});

const crmRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/crm',
  component: CRMPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/projects',
  component: ProjectsPage,
});

const estimatesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/estimates',
  component: EstimatesPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/documents',
  component: DocumentsPage,
});

const aiRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/ai',
  component: AIPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  authLayout.addChildren([loginRoute, registerRoute]),
  protectedLayout.addChildren([
    dashboardRoute,
    crmRoute,
    projectsRoute,
    estimatesRoute,
    documentsRoute,
    aiRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
